import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Loader2, User, TrendingUp, TrendingDown, Wallet, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const typeColors = {
  opening: 'bg-gray-100 text-gray-600',
  earning: 'bg-green-100 text-green-700',
  payment: 'bg-blue-100 text-blue-700',
  expense: 'bg-orange-100 text-orange-700',
};

const DriverLedger = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(searchParams.get('driver_id') || '');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [settlementForm, setSettlementForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', payment_mode: 'NEFT', reference_number: '', remarks: '' });

  const handleSaveSettlement = async () => {
    if (!settlementForm.date || !settlementForm.amount) {
      toast({ title: 'Error', description: 'Date and amount are required', variant: 'destructive' });
      return;
    }
    setSavingSettlement(true);
    try {
      const payload = {
        ...settlementForm,
        amount: parseFloat(settlementForm.amount)
      };
      const res = await fetch(`${backendUrl}/api/drivers/${selectedDriverId}/settlements`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Settlement recorded successfully!' });
        setShowSettlementModal(false);
        setSettlementForm({ date: new Date().toISOString().split('T')[0], amount: '', payment_mode: 'NEFT', reference_number: '', remarks: '' });
        // Reload ledger
        setLoading(true);
        const ld = await fetch(`${backendUrl}/api/drivers/${selectedDriverId}/ledger`, { headers: getAuthHeaders() }).then(r => r.json());
        setLedger(ld);
      } else {
        toast({ title: 'Error', description: 'Failed to record settlement', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Server error', variant: 'destructive' });
    }
    setSavingSettlement(false);
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  useEffect(() => {
    fetch(`${backendUrl}/api/drivers?limit=500`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => setDrivers(data.items || []));
  }, [backendUrl, getAuthHeaders]);

  useEffect(() => {
    if (!selectedDriverId) { setLedger(null); return; }
    setLoading(true);
    fetch(`${backendUrl}/api/drivers/${selectedDriverId}/ledger`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setLedger(data); setLoading(false); })
      .catch(() => { toast({ title: 'Error', description: 'Failed to load ledger', variant: 'destructive' }); setLoading(false); });
  }, [selectedDriverId, backendUrl, getAuthHeaders, toast]);

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 h-9 w-9">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-text-dark font-manrope" data-testid="driver-ledger-title">Driver Ledger</h1>
            <p className="text-text-secondary font-ibm mt-0.5">Full transaction history across all trips</p>
          </div>
        </div>
        {selectedDriverId && ledger && (
          <Button onClick={() => setShowSettlementModal(true)} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm">
            Record Settlement
          </Button>
        )}
      </div>

      {/* Driver Selector */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-accent-blue" />
            <span className="font-semibold font-ibm text-text-dark">Select Driver</span>
          </div>
          <Select value={selectedDriverId || '_none_'} onValueChange={v => setSelectedDriverId(v === '_none_' ? '' : v)}>
            <SelectTrigger data-testid="driver-select" className="w-72 font-ibm">
              <SelectValue placeholder="Choose a driver..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none_">-- Select Driver --</SelectItem>
              {drivers.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} {d.mobile_1 ? `— ${d.mobile_1}` : ''} {d.vendor_name ? `(${d.vendor_name})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
        </div>
      )}

      {/* Ledger Content */}
      {ledger && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary font-ibm">Total Credit</p>
                  <p className="text-lg font-bold text-green-700 font-ibm">{fmt(ledger.summary.total_credit)}</p>
                  <p className="text-xs text-text-secondary font-ibm">Earnings</p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary font-ibm">Total Debit</p>
                  <p className="text-lg font-bold text-blue-700 font-ibm">{fmt(ledger.summary.total_debit)}</p>
                  <p className="text-xs text-text-secondary font-ibm">Paid + Expenses</p>
                </div>
              </div>
            </Card>
            <Card className={`border shadow-sm p-4 ${ledger.summary.net_balance >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ledger.summary.net_balance >= 0 ? 'bg-green-200' : 'bg-red-200'}`}>
                  <Wallet className={`w-5 h-5 ${ledger.summary.net_balance >= 0 ? 'text-green-800' : 'text-red-800'}`} />
                </div>
                <div>
                  <p className="text-xs text-text-secondary font-ibm">Net Balance</p>
                  <p className={`text-lg font-bold font-ibm ${ledger.summary.net_balance >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {fmt(Math.abs(ledger.summary.net_balance))}
                  </p>
                  <p className={`text-xs font-semibold font-ibm ${ledger.summary.net_balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {ledger.summary.pending_label}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary font-ibm">Driver</p>
                  <p className="text-sm font-bold text-text-dark font-ibm">{ledger.driver.name}</p>
                  <p className="text-xs text-text-secondary font-ibm">{ledger.driver.mobile}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Transaction Table */}
          <Card className="bg-white border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-dark font-manrope">
                Transaction Ledger ({ledger.transactions.length} entries)
              </h3>
              <Badge className="bg-gray-100 text-gray-700 border-0 font-ibm text-xs">
                {ledger.transactions.filter(t => t.type === 'earning').length} trips
              </Badge>
            </div>

            {ledger.transactions.length === 0 ? (
              <div className="py-12 text-center text-text-secondary font-ibm">No transactions found for this driver</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-ibm" data-testid="ledger-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['#', 'Date', 'Trip No.', 'Description', 'Debit (Dr)', 'Credit (Cr)', 'Balance', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ledger.transactions.map((t, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-xs text-text-secondary">{i + 1}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap text-text-dark">{t.date || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono font-semibold text-accent-blue">{t.trip_number}</span>
                          <Badge className={`ml-2 border-0 text-xs ${t.trip_status === 'ACTIVE' ? 'bg-green-100 text-green-700' : t.trip_status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {t.trip_status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Badge className={`border-0 text-xs ${typeColors[t.type] || 'bg-gray-100 text-gray-600'}`}>
                              {t.type}
                            </Badge>
                            <span className="text-xs text-text-dark">{t.description}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-red-600">
                          {t.debit ? fmt(t.debit) : ''}
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-green-700">
                          {t.credit ? fmt(t.credit) : ''}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold ${t.balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                            {fmt(Math.abs(t.balance))}
                          </span>
                          <span className={`ml-1 text-xs ${t.balance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {t.balance > 0 ? 'Cr' : t.balance < 0 ? 'Dr' : ''}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/fleet/trips/view/${t.trip_id}`)}
                            className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-md"
                            title="View Trip"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-text-secondary" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Footer */}
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-xs font-bold text-text-dark uppercase tracking-wider">Total</td>
                      <td className="px-4 py-3 text-sm font-bold text-red-600">{fmt(ledger.summary.total_debit)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-green-700">{fmt(ledger.summary.total_credit)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-bold ${ledger.summary.net_balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {fmt(Math.abs(ledger.summary.net_balance))}
                        </span>
                        <span className={`ml-1 text-xs font-semibold ${ledger.summary.net_balance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {ledger.summary.net_balance > 0 ? 'Cr' : ledger.summary.net_balance < 0 ? 'Dr' : '(Settled)'}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {!selectedDriverId && !loading && (
        <Card className="bg-white border border-gray-100 shadow-sm p-12 text-center">
          <User className="w-12 h-12 text-text-secondary/30 mx-auto mb-3" />
          <p className="text-text-secondary font-ibm">Select a driver to view their full ledger</p>
        </Card>
      )}
      <Dialog open={showSettlementModal} onOpenChange={setShowSettlementModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-manrope">Record Settlement Payment</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 font-ibm text-sm">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={settlementForm.date} onChange={e => setSettlementForm({ ...settlementForm, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Amount (Rs.)</Label>
                {ledger && ledger.summary && (
                  <span className={`text-xs font-semibold ${ledger.summary.net_balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Pending: {fmt(Math.abs(ledger.summary.net_balance))} ({ledger.summary.pending_label})
                  </span>
                )}
              </div>
              <Input type="number" placeholder="e.g. 5000 or -2000" value={settlementForm.amount} onChange={e => setSettlementForm({ ...settlementForm, amount: e.target.value })} />
              <p className="text-[10px] text-gray-500">Enter a positive number if Company is paying the Driver. Enter a negative number if Driver is returning cash to the Company.</p>
            </div>
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={settlementForm.payment_mode} onValueChange={v => setSettlementForm({ ...settlementForm, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEFT">NEFT / RTGS</SelectItem>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference Number (Optional)</Label>
              <Input placeholder="UTR / Cheque No." value={settlementForm.reference_number} onChange={e => setSettlementForm({ ...settlementForm, reference_number: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea placeholder="Any notes..." value={settlementForm.remarks} onChange={e => setSettlementForm({ ...settlementForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettlementModal(false)} className="font-ibm">Cancel</Button>
            <Button onClick={handleSaveSettlement} disabled={savingSettlement} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm">
              {savingSettlement ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverLedger;
